import moment from 'moment/moment';
import { STATUS_ENUM } from '../../components/agv-status/status-enum';

// États d'un envoi AS400, identiques à ceux de l'API (TourImportantDatasState)
export const LOG_STATE = {
  PENDING: 'PENDING',
  AUTO_RETRY: 'AUTO_RETRY',
  INTERVENTION_REQUIRED: 'INTERVENTION_REQUIRED',
  DONE: 'DONE',
};

export const LOG_STATE_DEFINITIONS = {
  [LOG_STATE.INTERVENTION_REQUIRED]: {
    label: 'Action requise',
    description: 'Les essais automatiques sont terminés',
    status: STATUS_ENUM.NEGATIVE,
    color: 'tw-bg-status-negative',
  },
  [LOG_STATE.AUTO_RETRY]: {
    label: 'Nouvel essai prévu',
    description: 'Fleet réessaiera automatiquement',
    status: STATUS_ENUM.WARN,
    color: 'tw-bg-status-warn',
  },
  [LOG_STATE.PENDING]: {
    label: 'En attente d’envoi',
    description: 'Envoi dans la minute',
    status: STATUS_ENUM.INFO,
    color: 'tw-bg-status-info',
  },
  [LOG_STATE.DONE]: {
    label: 'Transmis',
    description: 'Reçu par l’AS400 Cooperl',
    status: STATUS_ENUM.POSITIVE,
    color: 'tw-bg-status-positive',
  },
};

export const ERROR_TYPE_LABELS = {
  METIER: 'Données refusées par l’AS400',
  TRANSITOIRE: 'AS400 indisponible ou sans réponse',
};

// Raison de l'arrêt des essais automatiques (enregistrée lors des envois)
export const STOP_REASON_LABELS = {
  NO_AUTO_RETRY:
    'Envoi antérieur à la mise en place des essais automatiques : pas de nouvel essai automatique',
  MAX_ATTEMPTS: 'Nombre maximum d’essais atteint',
  MANUAL_RETRY_FAILED: 'La relance manuelle a échoué',
};

/**
 * Raison pour laquelle Fleet ne réessaie plus un envoi en erreur (null s'il le relancera).
 * Les erreurs enregistrées avant FLEETC-3282 n'ont pas de stop_reason : il est déduit.
 */
export function getStopReason(log) {
  if (getLogState(log) !== LOG_STATE.INTERVENTION_REQUIRED) {
    return null;
  }
  const error =
    typeof log.last_error_if_any === 'object' ? log.last_error_if_any : null;
  let stopReason = error?.stop_reason;
  if (!stopReason) {
    if (Number(log.current_retry_count) >= Number(log.max_retry_count)) {
      stopReason = 'MAX_ATTEMPTS';
    } else {
      stopReason = 'NO_AUTO_RETRY';
    }
  }
  return stopReason;
}

export function getStopReasonLabel(log) {
  const stopReason = getStopReason(log);
  // Une relance manuelle n'a droit qu'à un essai : seul ce cas laisse un maximum de 1 essai
  if (stopReason === 'MAX_ATTEMPTS' && Number(log.max_retry_count) === 1) {
    return STOP_REASON_LABELS.MANUAL_RETRY_FAILED;
  }
  return STOP_REASON_LABELS[stopReason] || null;
}

export const HISTORY_EVENT_LABELS = {
  ATTEMPT: 'Envoi à l’AS400',
  MANUAL_RETRY: 'Nouvel envoi demandé',
  MANUAL_EDIT: 'Correction des données',
};

/**
 * État affiché d'un envoi, à partir de son statut et de ses compteurs de tentatives.
 */
export function getLogState(log) {
  if (!log) {
    return null;
  }
  if (log.status === 'DONE') {
    return LOG_STATE.DONE;
  }
  if (log.status === 'ERRORED') {
    const canBeRetried =
      Boolean(Number(log.can_be_retried)) &&
      Number(log.current_retry_count) < Number(log.max_retry_count);
    return canBeRetried
      ? LOG_STATE.AUTO_RETRY
      : LOG_STATE.INTERVENTION_REQUIRED;
  }
  return LOG_STATE.PENDING;
}

// Relance et correction manuelles : uniquement quand les essais automatiques sont terminés
export function canBeManuallyRetried(log) {
  return getLogState(log) === LOG_STATE.INTERVENTION_REQUIRED;
}

export function getLogStateDefinition(log) {
  return LOG_STATE_DEFINITIONS[getLogState(log)];
}

export function getLogStateLabel(log) {
  if (log?.status === 'IN_PROGRESS') {
    return 'Envoi en cours';
  }
  return getLogStateDefinition(log)?.label;
}

export function getLastErrorMessage(log) {
  const error = log?.last_error_if_any;
  if (!error) {
    return null;
  }
  if (typeof error === 'string') {
    return error;
  }
  return error.message || null;
}

export function formatDateTime(date) {
  return date ? moment(date).format('DD/MM/YYYY HH:mm') : '—';
}

export function formatDate(date) {
  return date ? moment(date).format('DD/MM/YYYY') : '—';
}

// "X / Y" comme dans l'alerte Teams : un envoi sans relance automatique n'a droit qu'à 1 tentative
export function formatAttempts(log) {
  const attempts = Number(log?.current_retry_count) || 0;
  if (getStopReason(log) === 'NO_AUTO_RETRY') {
    // Les anciennes lignes n'incrémentaient pas le compteur : elles ont été tentées une fois
    return `${Math.max(attempts, 1)} / 1`;
  }
  return `${attempts} / ${Number(log?.max_retry_count) || 0}`;
}

/**
 * Valide le texte saisi dans l'éditeur de payload.
 * Retourne { value } si le JSON est exploitable, { error } sinon.
 */
export function parsePayload(text) {
  let value;
  try {
    value = JSON.parse(text);
  } catch (error) {
    return { error: `JSON invalide : ${error.message}` };
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return { error: 'Les données doivent être un objet JSON.' };
  }
  if (!value.DLA_TOURNE) {
    return { error: 'Les données doivent contenir le bloc DLA_TOURNE.' };
  }
  return { value };
}

function flatten(value, path = '', result = {}) {
  if (value !== null && typeof value === 'object') {
    const entries = Array.isArray(value)
      ? value.map((item, index) => [`[${index}]`, item])
      : Object.entries(value).map(([key, item]) => [
          path ? `.${key}` : key,
          item,
        ]);
    if (!entries.length) {
      result[path] = Array.isArray(value) ? '[]' : '{}';
    }
    for (const [key, item] of entries) {
      flatten(item, `${path}${key}`, result);
    }
    return result;
  }
  result[path] = value;
  return result;
}

/**
 * Différences champ par champ entre deux payloads JSON.
 * Retourne [{ path, type: 'added' | 'removed' | 'changed', before, after }].
 */
export function computeJsonDiff(before, after) {
  const flatBefore = flatten(before ?? {});
  const flatAfter = flatten(after ?? {});
  const paths = new Set([
    ...Object.keys(flatBefore),
    ...Object.keys(flatAfter),
  ]);

  const diff = [];
  for (const path of paths) {
    const inBefore = Object.prototype.hasOwnProperty.call(flatBefore, path);
    const inAfter = Object.prototype.hasOwnProperty.call(flatAfter, path);
    if (inBefore && !inAfter) {
      diff.push({ path, type: 'removed', before: flatBefore[path] });
    } else if (!inBefore && inAfter) {
      diff.push({ path, type: 'added', after: flatAfter[path] });
    } else if (
      JSON.stringify(flatBefore[path]) !== JSON.stringify(flatAfter[path])
    ) {
      diff.push({
        path,
        type: 'changed',
        before: flatBefore[path],
        after: flatAfter[path],
      });
    }
  }
  return diff.sort((a, b) => a.path.localeCompare(b.path));
}

export function formatDiffValue(value) {
  if (value === undefined) {
    return '—';
  }
  return typeof value === 'string' ? `"${value}"` : String(value);
}

// Coloration du JSON, reprise de l'ancienne page : fond bleu nuit, clés en blanc,
// chaînes en orange, nombres en bleu
export const JSON_COLORS = {
  background: 'tw-bg-[#0e0e2c]',
  text: 'tw-text-[#999]',
  key: 'tw-text-white',
  string: 'tw-text-orange-300',
  number: 'tw-text-blue-300',
  literal: 'tw-text-violet-300',
  punctuation: 'tw-text-[#999]',
  // Pas de marge : le style global de <mark> (Bootstrap) décalerait le texte sous la zone de saisie
  match: 'tw-p-0 tw-bg-yellow-300/30 tw-text-inherit tw-rounded-sm',
  currentMatch: 'tw-p-0 tw-bg-yellow-300 tw-text-[#0e0e2c] tw-rounded-sm',
};

const JSON_TOKEN_PATTERN =
  /("(?:[^"\\n]|\.)*"?)(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b|([{}[\],:])|(\s+)|([^\s"{}[\],:]+)/g;

/**
 * Découpe un texte JSON (même invalide, en cours de saisie) en morceaux typés pour la coloration :
 * key, string, number, literal, punctuation ou text. Le texte d'origine est conservé tel quel.
 */
export function tokenizeJson(text) {
  const tokens = [];
  for (const match of String(text).matchAll(JSON_TOKEN_PATTERN)) {
    const [whole, quoted, colon, number, literal, punctuation] = match;
    if (quoted !== undefined) {
      tokens.push({ type: colon ? 'key' : 'string', text: quoted, start: match.index });
      if (colon) {
        tokens.push({ type: 'punctuation', text: colon, start: match.index + quoted.length });
      }
    } else if (number !== undefined) {
      tokens.push({ type: 'number', text: whole, start: match.index });
    } else if (literal !== undefined) {
      tokens.push({ type: 'literal', text: whole, start: match.index });
    } else if (punctuation !== undefined) {
      tokens.push({ type: 'punctuation', text: whole, start: match.index });
    } else {
      tokens.push({ type: 'text', text: whole, start: match.index });
    }
  }
  return tokens;
}

// Texte comparable : insensible à la casse et aux accents, même longueur que l'original
export const normalizeSearchText = (text) =>
  String(text).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Positions [start, end) de toutes les occurrences de search dans text (casse et accents ignorés). */
export function findTextMatches(text, search) {
  const needle = normalizeSearchText(search.trim());
  if (!needle) {
    return [];
  }
  const haystack = normalizeSearchText(text);
  const matches = [];
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    matches.push({ start: index, end: index + needle.length });
    index = haystack.indexOf(needle, index + needle.length);
  }
  return matches;
}
